import React, { useState, type FC, type PropsWithChildren, useContext, type UIEvent } from 'react';

import { getColor, isDark } from '@lucid/theme';
import { withImage, useProductUrls, useCarouselScroll, useSmartlingStrings } from '@lucid/hooks';
import { useInView } from 'react-intersection-observer';

import type { Lucid } from '../../../../types/types';
import ControlButton from '../../../../components/buttons/control-button';
import ImageWithPlaceholder from '../../../../components/image/image-with-placeholder';
import Box from '../../../../components/box/box';
import Container from '../../../../components/container/container';
import Heading from '../../../../components/heading/heading';
import LayoutContext from '../../../../components/context/layout-context';
import Link from '../../../../components/link/link';
import Text from '../../../../components/text/text';

export interface TemplateCarouselProps {
  /** The section background color */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The list of templates */
  cards: Lucid.Directus.Generated.Template_Card[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** The section heading */
  heading?: string;
  /** Theme variant for the section heading */
  headingVariant?: Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** The section link */
  link?: Lucid.Directus.Generated.Links;
  /** Allows to pass in a Beacon event to the CTA link */
  beaconEvent?: Lucid.Beacon.Event;
  /** The subheading text */
  text?: string;
}

const TemplateCarousel: FC<PropsWithChildren<TemplateCarouselProps>> = ({
  backgroundColor = 'coolGrey.0',
  cards,
  className,
  heading,
  headingVariant = 'heading2XL',
  link,
  text,
  beaconEvent,
}) => {
  const { shortCode, urlLangPrefix, product } = useContext(LayoutContext);
  const { registerUrl } = useProductUrls(urlLangPrefix, product);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_consideration_template', 'mktg_carousel_slide_previous', 'mktg_carousel_slide_next'],
    shortCode,
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const backgroundColorValue = (typeof backgroundColor === 'object' && backgroundColor[product]) || backgroundColor;
  const hasDarkBackground = isDark(backgroundColorValue);

  const indicesLength = cards.length / 2;
  const [inViewRef, isInView] = useInView({ threshold: 1.0 });

  const { scrollRef, handleScroll, updateCarousel } = useCarouselScroll({
    numSlides: indicesLength,
    handleUpdateCarousel: setActiveIndex,
    itemWidth: 204,
  });

  return (
    <Box
      css={theme => ({
        label: 'templateCarousel',
        backgroundColor: getColor({
          color: backgroundColor,
          theme,
        }),
        padding: `${theme.space.s80}px 0`,
        overflow: 'hidden',
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container>
        <Box
          css={theme => ({
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            [theme.breakpoints.bp1024.min]: {
              gridTemplateColumns: '520px 1fr',
              gridColumnGap: theme.space.s24,
            },
          })}
        >
          <Box
            css={theme => ({
              zIndex: theme.zIndices.z500,
              paddingBottom: theme.space.s80,
              position: 'relative',
              width: '520px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              [theme.breakpoints.bp1024.min]: {
                paddingBottom: 0,
                '&::before': {
                  content: '""',
                  backgroundColor: getColor({
                    color: backgroundColor,
                    theme,
                  }),
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: theme.zIndices.zNegative1,
                  minHeight: '400px',
                  height: '100%',
                  width: '1000px',
                  display: 'block',
                },
              },
            })}
          >
            <Heading
              as="h2"
              css={theme => [hasDarkBackground && { color: theme.colors.white }]}
              variant={headingVariant}
            >
              {heading}
            </Heading>
            <Text variant="paragraphM400">{text}</Text>
            {link && (
              <Link css={{ fontSize: '20px' }} href={link.final_url} iconAnimation="navigational" variant="primary">
                {link.default_link_text}
              </Link>
            )}
          </Box>
          <Box
            ref={scrollRef}
            css={theme => ({
              display: 'grid',
              overflowX: 'scroll',
              scrollSnapType: 'x mandatory',
              width: '100vw',
              padding: `${theme.space.s24}px ${theme.space.s24}px ${theme.space.s48}px ${theme.space.s24}px`,
              marginLeft: '-1.865rem',
              rowGap: theme.space.s48,
              columnGap: theme.space.s24,
              gridTemplateRows: 'repeat(2, auto)',
              gridAutoFlow: 'column',
              alignContent: 'center',
              zIndex: theme.zIndices.z10! + 5,
              scrollPaddingInlineStart: theme.space.s24,
              [theme.breakpoints.bp900.min]: {
                marginLeft: '-3.75rem',
              },
              [theme.breakpoints.bp1024.min]: {
                marginLeft: 0,
                width: 'calc(100% + (100vw - 544px - 100%) / 2)',
                paddingRight: '1.75rem',
              },
              [theme.breakpoints.bp1220.min]: {
                paddingRight: '3.75rem',
              },
              [theme.breakpoints.bp1440.min]: {
                paddingRight: '7.5rem',
              },
            })}
            onScroll={e => handleScroll(e as UIEvent<HTMLDivElement>)}
          >
            {cards.map((card: Lucid.Directus.Generated.Template_Card, index: number) => (
              <Link
                key={`template-card-carousel-${index}`}
                ref={index === cards?.length - 1 ? inViewRef : null}
                aria-label={withSmartling('mktg_consideration_template', {
                  template: card.heading || '',
                })}
                beaconEvent={beaconEvent}
                css={theme => ({
                  scrollSnapAlign: 'center',
                  scrollSnapStop: 'always',
                  height: '180px',
                  width: '180px',
                  position: 'relative',
                  borderRadius: theme.radii.r4,
                  [theme.breakpoints.bp900.min]: {
                    scrollSnapAlign: 'start',
                  },
                  [theme.breakpoints.bp1024.min]: {
                    '&:hover > div:first-of-type, &:focus > div:first-of-type,': {
                      visibility: 'visible',
                      borderColor: theme.colors.coolGrey[70],
                    },
                  },
                })}
                href={card?.link?.final_url || `${registerUrl}/${card?.template_id}`}
              >
                <Box
                  css={theme => ({
                    height: '180px',
                    width: '180px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: theme.zIndices.z10! + 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: theme.space.s16,
                    textAlign: 'center',
                    borderRadius: theme.radii.r4,
                    border: `1px solid ${theme.colors.coolGrey[30]}`,
                    visibility: 'hidden',
                  })}
                >
                  <Text css={{ margin: 0 }} variant="labelS600">
                    {card.heading}
                  </Text>
                </Box>
                <Box
                  css={theme => ({
                    backgroundColor: theme.colors.white,
                    height: '180px',
                    width: '180px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: theme.zIndices.z10,
                    borderRadius: theme.radii.r4,
                  })}
                >
                  <ImageWithPlaceholder
                    isBackgroundImage
                    alt=""
                    aspectRatio="1:1"
                    src={
                      withImage(card?.image) ||
                      `https://cdn.lucid.app/documents/thumb/${card?.template_id}/0/644/NULL/750`
                    }
                  />
                  <Text
                    css={theme => ({
                      margin: 0,
                      padding: theme.space.s4,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '180px',
                      [theme.breakpoints.bp1024.min]: { display: 'none' },
                    })}
                    variant="labelXS500"
                  >
                    {card.heading}
                  </Text>
                </Box>
              </Link>
            ))}
          </Box>
        </Box>
        <Box
          css={theme => ({
            display: 'flex',
            gap: theme.space.s24,
            justifyContent: 'end',
            paddingTop: theme.space.s40,
            paddingRight: theme.space.s40,
          })}
        >
          <ControlButton
            aria-label={(
              withSmartling('mktg_carousel_slide_previous', {
                slide: `${activeIndex === 0 ? 1 : activeIndex}`,
              }) || 'Go to previous slide'
            ).concat(' Press Up to view card')}
            css={{ transform: 'rotate(180deg)', gridArea: 'backButton' }}
            hasDarkBackground={hasDarkBackground}
            iconType="ArrowRight"
            isDisabled={activeIndex === 0}
            onClick={() => updateCarousel(activeIndex > 0 ? activeIndex - 1 : activeIndex)}
            onKeyDown={event => {
              if (['Enter', 'Space'].includes(event.code)) {
                updateCarousel(activeIndex > 0 ? activeIndex - 1 : activeIndex);
              }
            }}
          />
          <ControlButton
            aria-label={(
              withSmartling('mktg_carousel_slide_next', {
                slide: `${activeIndex < indicesLength - 1 ? activeIndex + 2 : indicesLength}`,
              }) || 'Go to next slide'
            ).concat(' Press Up to view card')}
            css={{ gridArea: 'nextButton' }}
            hasDarkBackground={hasDarkBackground}
            iconType="ArrowRight"
            isDisabled={activeIndex === indicesLength - 1 || isInView}
            onClick={() => updateCarousel(activeIndex < indicesLength - 1 ? activeIndex + 1 : activeIndex)}
            onKeyDown={event => {
              if (['Enter', 'Space'].includes(event.code)) {
                updateCarousel(activeIndex < indicesLength - 1 ? activeIndex + 1 : activeIndex);
              }
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export { TemplateCarousel };
export default TemplateCarousel;
